import { Icon, Tooltip } from 'components/common';
import { useMemo, useState } from 'react';
import { columnName as strings } from '../strings';
import { EditorRow, SmallText } from '../styled';
import { RevertableInput } from './RevertableInput';
import { createColumnNameValidator } from './validation';

import { useDispatch, useSelector } from 'react-redux';
import {
  exportTemplateActions as actions,
  exportTemplateSelectors as selectors,
} from 'reducers/exportTemplate/reducer';

const editIconStyle = {
  cursor: 'pointer',
  color: '#8d8d8d',
  fontSize: 12,
  marginLeft: 4,
};

/**
 * A specialised input for editing the name of a column within the columns list.
 * @param {Object} props
 * @param {string} props.sheetMetadataId
 * @param {string} props.columnMetadataId
 */
export const ColumnNameInput = ({ sheetMetadataId, columnMetadataId }) => {
  // ========== Redux state ==========

  const dispatch = useDispatch();
  const editingColumn = useSelector(state =>
    selectors.getDraftColumn(state, columnMetadataId)
  );
  const columnMap = useSelector(selectors.getDraftSheetColumnMap);
  const columnOrder = useSelector(selectors.getDraftSheetColumnOrder);

  // ========== Local UI state ==========

  const [editingColumnName, setEditingColumnName] = useState(false);

  // Build the list of *other* columns' display names so the validator can
  // reject case-insensitive duplicates. Excludes the column being edited so
  // a no-op rename does not collide with itself.
  const validate = useMemo(() => {
    const otherDisplayNames = [];
    if (columnMap && columnOrder) {
      for (const colId of columnOrder) {
        if (colId === columnMetadataId) continue;
        const col = columnMap[colId];
        if (!col) continue;
        const name = col.overridenName || col.defaultName;
        if (name) otherDisplayNames.push(name);
      }
    }
    return createColumnNameValidator(otherDisplayNames);
  }, [columnMap, columnOrder, columnMetadataId]);

  // ========== Derived state ==========

  if (!editingColumn) return null;

  const defaultName = editingColumn.defaultName;
  const displayName = editingColumn.overridenName || defaultName;
  const hasModifiedName =
    editingColumn.overridenName && editingColumn.overridenName !== defaultName;

  // ========== Event handlers ==========

  /** @param {string} newValue */
  const onNewValueConfirmed = newValue => {
    setEditingColumnName(false);

    const overridenName =
      newValue && newValue !== defaultName ? newValue : null;
    dispatch(
      actions.editingColumnNameChanged({
        sheetId: sheetMetadataId,
        columnId: columnMetadataId,
        overridenName,
      })
    );
  };

  // ========== Read-only render ==========

  if (!editingColumnName) {
    return (
      <EditorRow $flex>
        <Tooltip
          title={hasModifiedName ? strings.originalName(defaultName) : ''}
        >
          <span style={{ fontSize: 12 }}>{displayName}</span>
        </Tooltip>
        {hasModifiedName && <SmallText $mt={2}>{strings.edited}</SmallText>}
        <span style={editIconStyle} onClick={() => setEditingColumnName(true)}>
          <Icon icon='edit' size='1x' color='#8D8D8D' />
        </span>
      </EditorRow>
    );
  }

  // ========== Editing render ==========

  return (
    <EditorRow $flex>
      <RevertableInput
        currentValue={displayName}
        revertValue={defaultName}
        onNewValueConfirmed={onNewValueConfirmed}
        validate={validate}
      />
    </EditorRow>
  );
};
